/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxFrag */
import React, { useCallback } from 'react';

import { css, jsx } from '@atlaskit/css';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';

import type { AIDefinitionsPlugin } from '../aiDefinitionsPluginType';
import { closeReadingAidsPopup } from '../pm-plugins/ai-definitions/commands';
import type { ReadingAidsPopup, ReadingAidsPopupPropsInState } from '../types';

import { AIDefinitionsGlobalStyles } from './GlobalStyles';
import { READING_AIDS_POPUP_PORTAL_CONTAINER } from './utils/const';
import { useManageSelectionToolbarVisibility } from './utils/hooks';

const readingAidsPopupPortalContainerStyle = css({
	position: 'relative',
});

type CanShowReadingAidsPopup =
	| {
			contentId: undefined;
			isReadingAidsPopupOpen: false;
			ReadingAidsPopup: undefined;
			readingAidsPopupKey: undefined;
			readingAidsPopupProps: undefined;
			show: false;
	  }
	| {
			// need to add the following properties to make typescript happy when accessing the properties
			contentId: string;
			isReadingAidsPopupOpen: true;
			ReadingAidsPopup: ReadingAidsPopup;
			readingAidsPopupKey: string;
			readingAidsPopupProps: ReadingAidsPopupPropsInState;
			show: true;
	  };

function canShowReadingAidsPopup({
	props,
	enabled,
	autoHighlightEnabled,
	readingAidsPopupProps,
	readingAidsPopupKey,
	isReadingAidsPopupOpen,
}: {
	autoHighlightEnabled?: boolean;
	enabled?: boolean;
	isReadingAidsPopupOpen?: boolean;
	props: AIDefinitionsProps;
	readingAidsPopupKey?: string;
	readingAidsPopupProps?: ReadingAidsPopupPropsInState;
}): CanShowReadingAidsPopup {
	const { contentId, ReadingAidsPopup, editorView } = props;
	if (
		editorView &&
		enabled !== undefined &&
		autoHighlightEnabled !== undefined &&
		contentId &&
		ReadingAidsPopup &&
		isReadingAidsPopupOpen &&
		readingAidsPopupKey &&
		readingAidsPopupProps
	) {
		return {
			show: true,
			contentId,
			ReadingAidsPopup,
			isReadingAidsPopupOpen,
			readingAidsPopupKey,
			readingAidsPopupProps,
		};
	}
	return {
		show: false,
		contentId: undefined,
		ReadingAidsPopup: undefined,
		isReadingAidsPopupOpen: false,
		readingAidsPopupKey: undefined,
		readingAidsPopupProps: undefined,
	};
}

export type AIDefinitionsProps = {
	// api is mandatory but can be undefined
	api: ExtractInjectionAPI<AIDefinitionsPlugin> | undefined;
	contentId?: string;
	editorView: EditorView;
	ReadingAidsPopup?: ReadingAidsPopup;
};

/**
 * readingAidsPopupKey
 * - When there are multiple auto-highlights in the document,
 * - and the user clicks on one auto-highlight to trigger the ReadingAidsPopup, then click another.
 * - readingAidsPopupKey will prevent the first ReadingAidsPopup from switching to the loading state
 * - and let the second ReadingAidsPopup show loading state.
 *
 * - Right now we're hiding the first ReadingAidsPopup when we click the second one.
 * - But readingAidsPopupKey will save us an experiment check if we want to show the second ReadingAidsPopup in the future.
 */
export const AIDefinitions = (props: AIDefinitionsProps): JSX.Element | null => {
	const { api, editorView } = props;
	const {
		enabled: sharedEnabled,
		autoHighlightEnabled: sharedAutoHighlightEnabled,
		readingAidsPopupProps: sharedReadingAidsPopupProps,
		readingAidsPopupKey: sharedReadingAidsPopupKey,
		isReadingAidsPopupOpen: sharedIsReadingAidsPopupOpen,
		mode,
	} = useSharedPluginStateWithSelector(api, ['aiDefinitions', 'editorViewMode'], (states) => {
		return {
			enabled: states.aiDefinitionsState?.enabled,
			autoHighlightEnabled: states.aiDefinitionsState?.autoHighlightEnabled,
			readingAidsPopupProps: states.aiDefinitionsState?.readingAidsPopupProps,
			readingAidsPopupKey: states.aiDefinitionsState?.readingAidsPopupKey,
			isReadingAidsPopupOpen: states.aiDefinitionsState?.isReadingAidsPopupOpen,
			mode: states.editorViewModeState?.mode,
		};
	});

	const defaultOnResult = useCallback(() => {}, []);

	const onLoad = useCallback(() => {
		sharedReadingAidsPopupProps?.onLoad?.();
		// maybe log analytics event here in the future if needed
	}, [sharedReadingAidsPopupProps]);

	const defaultOnClose = useCallback(() => {
		closeReadingAidsPopup()(editorView.state, editorView.dispatch);
	}, [editorView]);

	const { show, contentId, ReadingAidsPopup, readingAidsPopupKey, readingAidsPopupProps } =
		canShowReadingAidsPopup({
			props,
			enabled: sharedEnabled,
			autoHighlightEnabled: sharedAutoHighlightEnabled,
			readingAidsPopupProps: sharedReadingAidsPopupProps,
			readingAidsPopupKey: sharedReadingAidsPopupKey,
			isReadingAidsPopupOpen: sharedIsReadingAidsPopupOpen,
		});

	useManageSelectionToolbarVisibility({ api, show });

	if (!sharedEnabled || !sharedAutoHighlightEnabled) {
		return null;
	}

	if (!fg('platform_editor_ai_aifc_streaming')) {
		// Summarize popups are triggered from the smartlink floating toolbar,
		// which only shows in edit mode — allow them to render here regardless
		// of the view-mode gate (only when the smartlink-toolbar experiment is
		// on, since that's the only surface that produces summarize-mode popups).
		const isSummarizeMode =
			sharedReadingAidsPopupProps?.mode === 'summarize' &&
			expValEqualsNoExposure('platform_editor_rovobutton_smartlink_toolbar_exp', 'isEnabled', true);
		if (mode !== 'view' && !isSummarizeMode) {
			return null;
		}
	}

	return (
		<>
			<AIDefinitionsGlobalStyles />
			<div id={READING_AIDS_POPUP_PORTAL_CONTAINER} css={readingAidsPopupPortalContainerStyle} />
			{show ? (
				<ReadingAidsPopup
					key={readingAidsPopupKey}
					// Ignored via go/ees005
					// eslint-disable-next-line react/jsx-props-no-spreading
					{...readingAidsPopupProps}
					isReadingAidsOpen
					popupPortalContainerId={READING_AIDS_POPUP_PORTAL_CONTAINER}
					contentId={contentId}
					onResult={readingAidsPopupProps?.onResult || defaultOnResult}
					onLoad={onLoad}
					onClose={readingAidsPopupProps?.onClose || defaultOnClose}
				/>
			) : null}
		</>
	);
};
